import React, { useEffect, useMemo, useState } from 'react';
import { ventes as api } from '../services/api';
import {
  nb, eur, eurCourt, pct1, signe, signePct, CelluleDelta,
  useFiltresVentes, PanneauVentes, usePanneau, basculer,
} from './ventesUtils';
import {
  SelecteurMaille, useRegroupement, construireArbre, axesDisponibles,
} from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';

const TITRES = { vn: 'VN — Performance', vo: 'VO — Performance' };
// Mêmes axes, mêmes libellés et même ordre que le bloc de répartition des
// rapports au jour le jour : un même mot doit désigner la même chose d'un
// écran à l'autre. Les cinq agrégats arrivent ensemble, la bascule est donc
// instantanée.
const AXES = [
  { c: 'marque', lib: 'marque', col: 'Marque' },
  // VN seulement : en VO l'export n'a pas de ligne de modèle, sa désignation
  // complète (« NEW 208 5 PORTES 12L LIKE 75CV BVM5 ») ferait un millier de
  // lignes.
  { c: 'modele', lib: 'modèle', col: 'Modèle', vnSeul: true },
  { c: 'vendeur', lib: 'vendeur', col: 'Vendeur' },
  { c: 'famille', lib: 'type de client', col: 'Type de client' },
  { c: 'segment', lib: 'type de stock', col: 'Type de stock' },
  { c: 'age', lib: 'âge de stock', col: 'Âge de stock' },
];

// Écart N-1 formaté (valeur absolue signée + pourcentage).
function Delta({ abs, pct, pt, unite }) {
  const val = pct != null ? pct : pt;
  if (val == null) return <div className="d"><small>pas d'historique N-1</small></div>;
  const pos = val >= 0;
  return (
    <div className={`d ${pos ? 'pos' : 'neg'}`}>
      {abs != null && `${signe(abs)} · `}
      {pt != null ? `${pos ? '+' : ''}${pct1(pt).replace(' %', ' pt')}` : signePct(pct)}
      <small> vs N-1{unite ? ` · ${unite}` : ''}</small>
    </div>
  );
}

// `branche` choisit le mart côté serveur (Antilles par défaut, Auto Métropole
// pour les rapports am-*). Quand la marge n'est pas publiée pour la branche
// (VN métropole), le serveur renvoie margeDisponible=false et l'écran masque
// KPIs et colonnes de marge plutôt que d'afficher des zéros.
export default function VentesPerformance({ type = 'vn', branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, toggle] = usePanneau();
  // Axe du bloc d'analyse — réglage d'affichage, donc en en-tête de carte.
  const [axe, setAxe] = useState('marque');
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);

  useEffect(() => {
    let actif = true; setErreur('');
    api.performance(type, { periode: f.periode, mois: f.moisStr, societes: f.societesUrl, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [type, branche, f.periode, f.moisStr, f.societesUrl]);

  // Maille d'affichage du bloc « Par société » : concession, site, plaque.
  // Cliquer une ligne filtre TOUTE la page, y compris les autres tableaux :
  // le filtre `societes` accepte une liste, un regroupement s'y exprime donc
  // sans mécanisme supplémentaire.
  const maille = useRegroupement();
  const feuilles = useMemo(() => (data?.parSociete || []).map((s) => ({ ...s, libelle: s.nom, code: s.code })), [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  // Les bons de commande viennent du CRM, pas de Datacar. La colonne n'existe
  // que s'il y en a : une colonne de zéros ferait lire une absence d'activité
  // commerciale là où il n'y a qu'une absence de SAISIE. Le service l'éteint
  // aussi sous un filtre de segment, que la source ne sait pas honorer.
  // ⚠ Visible même à zéro (demande de l'utilisateur) : « aucun bon signé » est
  // une information, et une colonne intermittente se lit comme une panne.
  const avecBdc = !!data?.bdc?.dispo;
  const arbreSocietes = useMemo(
    () => construireArbre(feuilles, maille.niveau, (membres) => {
      const som = (f) => membres.reduce((t, m) => t + (f(m) || 0), 0);
      const vol = som((m) => m.volume.valeur), volN1 = som((m) => m.volume.n1);
      const ca = som((m) => m.ca.valeur), caN1 = som((m) => m.ca.n1);
      const mg = som((m) => m.marge?.valeur), mgN1 = som((m) => m.marge?.n1);
      // Taux hors ventes en mode agent : opérandes fournis par le serveur.
      const tc = som((m) => m.taux?.ca ?? m.ca.valeur), tm = som((m) => m.taux?.mg ?? m.marge?.valeur);
      const tcN1 = som((m) => m.taux?.caN1 ?? m.ca.n1), tmN1 = som((m) => m.taux?.mgN1 ?? m.marge?.n1);
      const pct = (a, b) => (b ? ((a - b) / Math.abs(b)) * 100 : null);
      return {
        bdc: som((m) => m.bdc),
        volume: { valeur: vol, n1: volN1, deltaPct: pct(vol, volN1) },
        ca: { valeur: ca, n1: caN1, deltaPct: pct(ca, caN1) },
        marge: { valeur: mg, n1: mgN1, deltaPct: pct(mg, mgN1) },
        margeUnit: { valeur: vol ? Math.round(mg / vol) : null, n1: volN1 ? Math.round(mgN1 / volN1) : null,
          deltaPct: pct(vol ? mg / vol : 0, volN1 ? mgN1 / volN1 : 0) },
        taux: { valeur: tc ? (tm / tc) * 100 : null,
          deltaPt: (tc && tcN1) ? ((tm / tc) - (tmN1 / tcN1)) * 100 : null },
      };
    }, maille.ouverts, (x) => x.ca?.valeur),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const k = data.kpis;
  const lignesAxe = (data.parAxe || {})[axe] || [];
  const AXE = AXES.find((a) => a.c === axe) || AXES[0];
  // La marque est la seule dimension que la source des bons partage avec les
  // ventes livrées.
  const bdcAxe = avecBdc && AXE.c === 'marque';
  // ⚠ La colonne retirée doit DIRE pourquoi : sans note, son absence se lit
  // comme un oubli.
  const bdcVentilable = AXE.c === 'marque';
  // Stock en regard (2026-09-30) : sur marque, modèle, type et âge de stock
  // seulement — un stock n'a ni vendeur ni type de client.
  const stockAxe = !!data.stock?.dispo && (data.stock.axes || []).includes(AXE.c);
  const arreteStock = data.stock?.arrete ? data.stock.arrete.split('-').reverse().join('/') : null;
  // Une référence présente en stock sans vente sur la période : ses montants
  // de vente sont sans objet, pas nuls.
  const sansVente = (m) => stockAxe && !m.volume && !m.ca;
  const negs = data.detail.filter((d) => d.marge < 0).length;
  const nbSoc = f.selected.length;
  const avecMarge = data.margeDisponible !== false;

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>{TITRES[type]}</h1>
          </div>
          <div className="vt-asof"><b>{data.periode}</b><br />groupe · {nbSoc} société{nbSoc > 1 ? 's' : ''}</div>
        </div>

        <div className="vt-kpis">
          <div className="vt-kpi"><div className="l">{type === 'vn' ? 'VN' : 'VO'} vendus</div><div className="v">{nb(k.volume.valeur)}</div><Delta abs={k.volume.delta} pct={k.volume.deltaPct} /></div>
          <div className="vt-kpi"><div className="l">CA HT</div><div className="v">{eurCourt(k.ca.valeur)}</div><Delta pct={k.ca.deltaPct} /></div>
          {avecMarge ? (
            <>
              <div className="vt-kpi"><div className="l">Marge</div><div className="v">{eurCourt(k.marge.valeur)}</div><Delta pct={k.marge.deltaPct} /></div>
              <div className="vt-kpi"><div className="l">Taux de marge</div><div className="v">{pct1(k.taux.valeur)}</div><Delta pt={k.taux.deltaPt} /></div>
              <div className="vt-kpi"><div className="l">Marge unitaire</div><div className="v">{eur(k.margeUnit.valeur)}</div><Delta pct={k.margeUnit.deltaPct} /></div>
            </>
          ) : (
            <div className="vt-kpi"><div className="l">CA TTC</div><div className="v">{eurCourt(k.caTtc?.valeur)}</div><Delta pct={k.caTtc?.deltaPct} /></div>
          )}
        </div>

        <p className="vt-insight">
          {!avecMarge ? (
            <>Volume {signePct(k.volume.deltaPct)}, CA HT {signePct(k.ca.deltaPct)} vs N-1. <b>Marge non disponible</b> : la structure de marge n'est renseignée que pour une minorité de sociétés dans l'export, elle n'est donc pas publiée.</>
          ) : k.taux.deltaPt != null && k.taux.deltaPt >= 0 && k.volume.deltaPct != null && k.volume.deltaPct < 0 ? (
            <>La marge <b>résiste</b> (taux {signePct(k.taux.deltaPt).replace(' %', ' pt')}, marge unitaire {signePct(k.margeUnit.deltaPct)}) malgré un <b>volume en recul</b> ({signePct(k.volume.deltaPct)} vs N-1) — moins de véhicules vendus mais mieux margés.</>
          ) : (
            <>Volume {signePct(k.volume.deltaPct)}, taux de marge {pct1(k.taux.valeur)} ({k.taux.deltaPt != null ? signePct(k.taux.deltaPt).replace(' %', ' pt') : '—'} vs N-1).</>
          )}
        </p>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Par société</h2>
            <div className="st-tools">
              <span className="hint">
                {avecMarge && data.margeCouverture != null && data.margeCouverture < 99
                  ? `marge connue sur ${data.margeCouverture} % du CA` : ''}</span>
              <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
          <table className="vt-tbl">
            <thead><tr>
              <th className="l">Société</th>
              {avecBdc && <th>BDC</th>}
              <th>Vol.</th><th className="vt-dh">vs N-1</th>
              <th>CA HT</th><th className="vt-dh">vs N-1</th>
              {avecMarge && <><th>Marge</th><th className="vt-dh">vs N-1</th><th>Marge unit.</th><th className="vt-dh">vs N-1</th><th>Taux</th><th className="vt-dh">vs N-1</th></>}
            </tr></thead>
            <tbody>
              {arbreSocietes.map((s) => {
                const cible = s.feuille ? s.code : (s.membres || []).join(',');
                return (
                <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                    onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                  <td className="l">
                    <span className="rg-in" style={{ width: s.niveau * 14 }} />
                    {s.feuille ? <span className="rg-chev vide" /> : (
                      <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                        title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
                        aria-label="Déplier"
                        onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                    )}
                    {s.libelle || s.nom}
                    {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                  </td>
                  {avecBdc && <td>{s.bdc ? nb(s.bdc) : '·'}</td>}
                  <td>{nb(s.volume.valeur)}</td><CelluleDelta valeur={s.volume.valeur} n1={s.volume.n1} />
                  <td>{eurCourt(s.ca.valeur)}</td><CelluleDelta valeur={s.ca.valeur} n1={s.ca.n1} fmt={eurCourt} />
                  {avecMarge && <>
                    <td className={s.marge.valeur < 0 ? 'vt-neg' : ''}>{eurCourt(s.marge.valeur)}</td><CelluleDelta valeur={s.marge.valeur} n1={s.marge.n1} fmt={eurCourt} />
                    <td>{eur(s.margeUnit.valeur)}</td><CelluleDelta valeur={s.margeUnit.valeur} n1={s.margeUnit.n1} fmt={eur} />
                    <td className="vt-taux">{pct1(s.taux.valeur)}</td><CelluleDelta pt={s.taux.deltaPt} />
                  </>}
                </tr>
                );
              })}
            </tbody>
            <tfoot><tr>
              <td className="l">Total</td>
              {avecBdc && <td>{nb(data.bdc.total)}</td>}
              <td>{nb(k.volume.valeur)}</td><CelluleDelta valeur={k.volume.valeur} n1={k.volume.n1} />
              <td>{eurCourt(k.ca.valeur)}</td><CelluleDelta valeur={k.ca.valeur} n1={k.ca.n1} fmt={eurCourt} />
              {avecMarge && <>
                <td>{eurCourt(k.marge.valeur)}</td><CelluleDelta valeur={k.marge.valeur} n1={k.marge.n1} fmt={eurCourt} />
                <td>{eur(k.margeUnit.valeur)}</td><CelluleDelta valeur={k.margeUnit.valeur} n1={k.margeUnit.n1} fmt={eur} />
                <td>{pct1(k.taux.valeur)}</td><CelluleDelta pt={k.taux.deltaPt} />
              </>}
            </tr></tfoot>
          </table>
        </div></div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Analyse par {AXE.lib}</h2>
            <div className="st-tools">
              {avecBdc && !bdcVentilable && (
                <span className="hint">⚠️ un bon de commande ne permet pas de ventiler par {AXE.lib}</span>
              )}
              {stockAxe && <span className="hint">stock : photo du {arreteStock || 'jour'}, hors période</span>}
              <div className="st-seg">
                {AXES.filter((a) => !a.vnSeul || type === 'vn').map((a) => {
                  // Un axe vide sur ce périmètre n'est pas proposé.
                  const off = !((data.parAxe || {})[a.c] || []).length;
                  return (
                    <button key={a.c} type="button" disabled={off}
                      className={axe === a.c ? 'on' : ''}
                      title={off ? `« ${a.col} » n'est renseigné sur aucune vente de la période` : ''}
                      onClick={() => setAxe(a.c)}>{a.lib}</button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Soixante-et-onze vendeurs pousseraient le reste hors de l'écran :
              le bloc défile dans son cadre, borné à une vingtaine de lignes,
              en-tête collé. */}
          <div className="vt-card-b vt-tbl-wrap vt-borne">
          <table className="vt-tbl">
            {/* ⚠ Hors de l'axe marque, la colonne n'est pas vide : elle est
                IMPOSSIBLE — un bon de commande n'a ni vendeur poussé, ni type
                de client, ni âge de stock. On la retire. */}
            <thead><tr><th className="l">{AXE.col}</th>{bdcAxe && <th>BDC</th>}<th>Volume</th>{stockAxe && <th title="Véhicules en stock aujourd'hui">Stock</th>}<th>CA HT</th>{avecMarge ? <><th>Marge</th><th>Marge unit.</th><th>Taux</th></> : <th>CA HT unit.</th>}</tr></thead>
            <tbody>
              {lignesAxe.map((m) => (
                <tr key={m.nom}>
                  <td className="l">{m.nom}</td>
                  {bdcAxe && <td>{m.bdc ? nb(m.bdc) : '·'}</td>}
                  <td>{m.volume ? nb(m.volume) : '·'}</td>
                  {stockAxe && <td>{m.stock ? nb(m.stock) : '·'}</td>}
                  {sansVente(m) ? (
                    <td colSpan={avecMarge ? 4 : 2} className="vt-dh">aucune vente sur la période</td>
                  ) : <>
                  <td>{eurCourt(m.ca)}</td>
                  {avecMarge ? (
                    <>
                      <td className={m.marge < 0 ? 'vt-neg' : ''}>{eurCourt(m.marge)}</td>
                      <td>{eur(m.margeUnit)}</td>
                      <td className="vt-taux" style={{ color: m.taux >= 15 ? 'var(--tbl-intensite)' : m.taux < 9 ? 'var(--tbl-defavorable)' : undefined }}>{pct1(m.taux)}</td>
                    </>
                  ) : <td>{m.volume ? eur(m.ca / m.volume) : '—'}</td>}
                  </>}
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td className="l">Total</td>{bdcAxe && <td>{nb(data.bdc.total)}</td>}<td>{nb(k.volume.valeur)}</td>{stockAxe && <td>{nb(data.stock.total)}</td>}<td>{eurCourt(k.ca.valeur)}</td>{avecMarge ? <><td>{eurCourt(k.marge.valeur)}</td><td>{eur(k.margeUnit.valeur)}</td><td>{pct1(k.taux.valeur)}</td></> : <td>{k.volume.valeur ? eur(k.ca.valeur / k.volume.valeur) : '—'}</td>}</tr></tfoot>
          </table>
        </div></div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Détail des ventes récentes</h2>
            {avecMarge && negs > 0 && <span className="hint">dont {negs} à marge négative</span>}</div>
          <div className="vt-card-b vt-tbl-wrap vt-borne">
          <table className="vt-tbl">
            <thead><tr><th className="l">Date</th><th className="l">Vendeur</th><th className="l">Marque</th><th className="l">Modèle</th>{avecMarge ? <><th>Marge</th><th>Taux</th></> : <th>CA HT</th>}<th>Âge (j)</th>{avecMarge && <th>Coût revient</th>}</tr></thead>
            <tbody>
              {/* Le cadre borne l'AFFICHAGE à une vingtaine de lignes ; on
                  garde les soixante que le serveur envoie, accessibles au
                  défilement, plutôt que d'en jeter trente arbitrairement. */}
              {data.detail.map((d, i) => (
                <tr key={i} className={avecMarge && d.marge < 0 ? 'vt-neg' : ''}>
                  <td className="l">{d.date ? d.date.split('-').reverse().join('/') : '—'}</td>
                  <td className="l">{d.vendeur}</td>
                  <td className="l">{d.marque}</td>
                  <td className="l" title={d.modele} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.modele}</td>
                  {avecMarge ? <><td>{eur(d.marge)}</td><td>{d.taux != null ? pct1(d.taux) : '—'}</td></> : <td>{eur(d.ca)}</td>}
                  <td>{d.joursStock != null ? nb(d.joursStock) : '—'}</td>
                  {avecMarge && <td>{eur(d.coutRevient)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      </main>
      {Panneau}
    </>
  );
}
